import React, { useState, useEffect, useCallback } from 'react';
import { FaFileExcel, FaSearch, FaCheckCircle, FaTimesCircle, FaClock } from 'react-icons/fa';
import ApplicationTable from '../components/applications/ApplicationTable';
import ApplicationDetailModal from '../components/applications/ApplicationDetailModal';
import ApprovalModal from '../components/applications/ApprovalModal';
import RejectionModal from '../components/applications/RejectionModal';
import { supabase } from '../services/supabase';
import { supabaseAdmin } from '../services/supabaseAdmin';
import adminService from '../services/admin.service';
import { useAuth } from '../context/AuthContext';

const exportToCSV = (rows, filename) => {
  if (!rows.length) return;
  const keys = Object.keys(rows[0]);
  const csv = [keys.join(','), ...rows.map(r => keys.map(k => `"${String(r[k] ?? '').replace(/"/g, '""')}"`).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

// Lisenziya sənədi private bucket-dədir — signed URL lazımdır
const getLicenseUrl = async (filePath) => {
  if (!filePath) return null;
  const { data, error } = await supabase.storage
    .from('business-licenses')
    .createSignedUrl(filePath, 3600); // 1 saatlıq keçərli URL
  if (error) return null;
  return data.signedUrl;
};

function ApplicationsPage() {
  const { admin } = useAuth();
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);

  // Fetch applications
  const fetchApplications = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('business_applications')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setApplications(data || []);
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Filter applications
  const filterApplications = useCallback(() => {
    let filtered = applications;

    // Search
    if (searchTerm) {
      filtered = filtered.filter(
        (app) =>
          app.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          app.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          app.voen?.includes(searchTerm)
      );
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter((app) => app.status === filterStatus);
    }

    setFilteredApplications(filtered);
  }, [applications, searchTerm, filterStatus]);

  // Initial fetch
  useEffect(() => {
    fetchApplications();

    // Real-time subscription
    const subscription = supabase
      .channel('applications_changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'business_applications' },
        (payload) => {
          console.log('Application changed:', payload);
          fetchApplications();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [fetchApplications]);

  // Filter when dependencies change
  useEffect(() => {
    filterApplications();
  }, [filterApplications]);

  // View detail handler
  const handleViewDetail = useCallback((application) => {
    setSelectedApplication(application);
    setShowDetailModal(true);
  }, []);

  // Approve handler
  const handleApprove = useCallback((application) => {
    setSelectedApplication(application);
    setShowApprovalModal(true);
  }, []);

  // Reject handler
  const handleReject = useCallback((application) => {
    setSelectedApplication(application);
    setShowRejectionModal(true);
  }, []);

  // Approval confirm handler
  const handleApprovalConfirm = async (subscriptionMonths) => {
    if (!selectedApplication) return;

    try {
      const subscriptionEnd = new Date();
      subscriptionEnd.setMonth(subscriptionEnd.getMonth() + subscriptionMonths);

      // Create businesses row — owner_id links to the auth user who applied
      const { data: businessData, error: businessError } = await supabase
        .from('businesses')
        .insert({
          name: selectedApplication.company_name,
          email: selectedApplication.email || selectedApplication.representative_email || '',
          description: '',
          business_type: selectedApplication.business_type || 'restaurant',
          cuisine_type: selectedApplication.cuisine_type || '',
          address: selectedApplication.address || '',
          city: selectedApplication.city || 'Bakı',
          phone: selectedApplication.phone || selectedApplication.representative_phone || '',
          total_capacity: selectedApplication.capacity || 50,
          target_audience: selectedApplication.target_audience || [],
          owner_id: selectedApplication.user_id,
          is_chain: selectedApplication.is_chain || selectedApplication.business_type === 'chain' || false,
          is_active: true,
          is_open: true,
          premium_tier: 1,
          subscription_start: new Date().toISOString(),
          subscription_end: subscriptionEnd.toISOString(),
        })
        .select()
        .single();

      if (businessError) {
        console.error('Business creation error:', businessError);
        throw businessError;
      }

      // For single cafe (not a chain), auto-create business_location so it appears in mobile app
      if (!selectedApplication.is_chain) {
        const { error: locError } = await supabase
          .from('business_locations')
          .insert({
            business_id:       businessData.id,
            name:              selectedApplication.company_name,
            address:           selectedApplication.address || '',
            city:              selectedApplication.city || 'Bakı',
            phone:             selectedApplication.phone || selectedApplication.representative_phone || '',
            cuisine:           selectedApplication.cuisine_type || '',
            business_type:     selectedApplication.business_type || 'cafe',
            total_capacity:    selectedApplication.capacity || 50,
            current_occupancy: 0,
            is_open:           true,
            premium_tier:      1,
            rating:            0,
            target_audience:   selectedApplication.target_audience || [],
            working_hours:     selectedApplication.working_hours || '',
          });

        if (locError) {
          console.error('Location creation error:', locError);
          // Non-blocking — business still created, location can be added manually
        }
      }

      // Update application status
      const { error: updateError } = await supabase
        .from('business_applications')
        .update({
          status: 'approved',
          admin_note: `Təsdiqləndi — ${subscriptionMonths} ay abunəlik`,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', selectedApplication.id);

      if (updateError) throw updateError;

      // Log action
      await adminService.logAction(
        admin?.id || 'super_admin',
        'approve_application',
        'business',
        businessData.id,
        {
          application_id: selectedApplication.id,
          company_name: selectedApplication.company_name,
          subscription_months: subscriptionMonths,
        }
      );

      // Send email notification
      try {
        await supabase.from('email_queue').insert({
          to_email: selectedApplication.email,
          subject: 'Müraciətiniz təsdiqləndi — Cafegoo',
          body: `Hörmətli ${selectedApplication.representative_name}, müraciətiniz təsdiqləndi. Giriş məlumatlarınız tezliklə göndəriləcək.`,
          status: 'pending',
          created_at: new Date().toISOString(),
        });
      } catch (_) {}

      alert('✅ Müraciət təsdiqləndi!\n\nBiznes hesabı yaradıldı. İstifadəçi artıq Business Panel-ə daxil ola bilər.');
      setShowApprovalModal(false);
      setSelectedApplication(null);
      fetchApplications();
    } catch (error) {
      console.error('Error approving application:', error);
      alert('Xəta baş verdi: ' + error.message);
    }
  };

  // Rejection confirm handler
  const handleRejectionConfirm = async (reason) => {
    if (!selectedApplication) return;

    try {
      const { error } = await supabase
        .from('business_applications')
        .update({
          status: 'rejected',
          admin_note: reason,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', selectedApplication.id);

      if (error) throw error;

      // Log action
      await adminService.logAction(
        admin.id,
        'reject_application',
        'business_application',
        selectedApplication.id,
        {
          company_name: selectedApplication.company_name,
          reason,
        }
      );

      // Send email notification
      try {
        await supabase.from('email_queue').insert({
          to_email: selectedApplication.email,
          subject: 'Müraciətiniz rədd edildi — Cafegoo',
          body: `Hörmətli ${selectedApplication.representative_name}, müraciətiniz rədd edildi. Səbəb: ${reason}`,
          status: 'pending',
          created_at: new Date().toISOString(),
        });
      } catch (_) {}

      alert('❌ Müraciət rədd edildi.');
      setShowRejectionModal(false);
      setSelectedApplication(null);
      fetchApplications();
    } catch (error) {
      console.error('Error rejecting application:', error);
      alert('Xəta baş verdi!');
    }
  };

  // Checkbox selection helpers
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredApplications.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredApplications.map(a => a.id)));
    }
  };

  // Bulk approve
  const handleBulkApprove = async () => {
    if (!selectedIds.size) return;
    const months = parseInt(prompt('Abunəlik müddəti (ay):', '1'), 10);
    if (!months || isNaN(months)) return;
    const items = filteredApplications.filter(a => selectedIds.has(a.id));
    for (const app of items) {
      setSelectedApplication(app);
      await new Promise(resolve => {
        // reuse existing approval logic inline
        (async () => {
          try {
            const subscriptionEnd = new Date();
            subscriptionEnd.setMonth(subscriptionEnd.getMonth() + months);
            const { data: businessData, error: businessError } = await supabase
              .from('businesses')
              .insert({
                name: app.company_name,
                email: app.email || app.representative_email || '',
                description: '',
                business_type: app.business_type || 'restaurant',
                cuisine_type: app.cuisine_type || '',
                address: app.address || '',
                city: app.city || 'Bakı',
                phone: app.phone || app.representative_phone || '',
                total_capacity: app.capacity || 50,
                target_audience: app.target_audience || [],
                owner_id: app.user_id,
                is_chain: app.is_chain || app.business_type === 'chain' || false,
                is_active: true,
                is_open: true,
                premium_tier: 1,
                subscription_start: new Date().toISOString(),
                subscription_end: subscriptionEnd.toISOString(),
              })
              .select()
              .single();
            if (!businessError && !app.is_chain) {
              await supabase.from('business_locations').insert({
                business_id: businessData.id,
                name: app.company_name,
                address: app.address || '',
                city: app.city || 'Bakı',
                phone: app.phone || app.representative_phone || '',
                cuisine: app.cuisine_type || '',
                business_type: app.business_type || 'cafe',
                total_capacity: app.capacity || 50,
                current_occupancy: 0,
                is_open: true,
                premium_tier: 1,
                rating: 0,
                target_audience: app.target_audience || [],
                working_hours: app.working_hours || '',
              });
            }
            await supabase.from('business_applications').update({
              status: 'approved',
              admin_note: `Təsdiqləndi — ${months} ay abunəlik`,
              reviewed_at: new Date().toISOString(),
            }).eq('id', app.id);
            await supabase.from('email_queue').insert({
              to_email: app.email,
              subject: 'Müraciətiniz təsdiqləndi — Cafegoo',
              body: `Hörmətli ${app.representative_name}, müraciətiniz təsdiqləndi. Giriş məlumatlarınız tezliklə göndəriləcək.`,
              status: 'pending',
              created_at: new Date().toISOString(),
            }).catch(() => {});
          } catch (e) { console.error('Bulk approve error:', e); }
          resolve();
        })();
      });
    }
    setSelectedIds(new Set());
    setSelectedApplication(null);
    fetchApplications();
    alert(`✅ ${items.length} müraciət təsdiqləndi!`);
  };

  // Bulk reject
  const handleBulkReject = async () => {
    if (!selectedIds.size) return;
    const reason = prompt('Rədd etmə səbəbi:');
    if (!reason) return;
    const items = filteredApplications.filter(a => selectedIds.has(a.id));
    for (const app of items) {
      try {
        await supabase.from('business_applications').update({
          status: 'rejected',
          admin_note: reason,
          reviewed_at: new Date().toISOString(),
        }).eq('id', app.id);
        await supabase.from('email_queue').insert({
          to_email: app.email,
          subject: 'Müraciətiniz rədd edildi — Cafegoo',
          body: `Hörmətli ${app.representative_name}, müraciətiniz rədd edildi. Səbəb: ${reason}`,
          status: 'pending',
          created_at: new Date().toISOString(),
        }).catch(() => {});
      } catch (e) { console.error('Bulk reject error:', e); }
    }
    setSelectedIds(new Set());
    fetchApplications();
    alert(`❌ ${items.length} müraciət rədd edildi!`);
  };

  // Loading state
  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner}></div>
        <p>Yüklənir...</p>
      </div>
    );
  }

  // Calculate stats
  const pendingCount = applications.filter((app) => app.status === 'pending').length;
  const approvedCount = applications.filter((app) => app.status === 'approved').length;
  const rejectedCount = applications.filter((app) => app.status === 'rejected').length;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>📝 Business Müraciətləri</h1>
          <p style={styles.subtitle}>Yeni biznes qeydiyyat müraciətlərini idarə edin</p>
        </div>
        <button style={styles.btnExport} onClick={() => exportToCSV(filteredApplications, 'applications.csv')}>
          <FaFileExcel /> Export Excel
        </button>
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div style={styles.bulkBar}>
          <span style={styles.bulkInfo}>{selectedIds.size} seçildi</span>
          <button onClick={handleBulkApprove} style={styles.bulkApprove}>✅ Hamısını Təsdiqlə</button>
          <button onClick={handleBulkReject} style={styles.bulkReject}>❌ Hamısını Rədd Et</button>
          <button onClick={() => setSelectedIds(new Set())} style={styles.bulkClear}>Ləğv Et</button>
        </div>
      )}

      {/* Stats */}
      <div style={styles.stats}>
        <div style={{ ...styles.statCard, borderLeft: '4px solid #F39C12' }}>
          <FaClock size={32} color="#F39C12" />
          <div>
            <p style={styles.statLabel}>Gözləyən:</p>
            <h3 style={styles.statValue}>{pendingCount}</h3>
          </div>
        </div>
        <div style={{ ...styles.statCard, borderLeft: '4px solid #27AE60' }}>
          <FaCheckCircle size={32} color="#27AE60" />
          <div>
            <p style={styles.statLabel}>Təsdiqlənmiş:</p>
            <h3 style={styles.statValue}>{approvedCount}</h3>
          </div>
        </div>
        <div style={{ ...styles.statCard, borderLeft: '4px solid #E74C3C' }}>
          <FaTimesCircle size={32} color="#E74C3C" />
          <div>
            <p style={styles.statLabel}>Rədd edilmiş:</p>
            <h3 style={styles.statValue}>{rejectedCount}</h3>
          </div>
        </div>
        <div style={{ ...styles.statCard, borderLeft: '4px solid #3498DB' }}>
          <div style={styles.iconText}>📊</div>
          <div>
            <p style={styles.statLabel}>Ümumi:</p>
            <h3 style={styles.statValue}>{applications.length}</h3>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={styles.filters}>
        <div style={styles.searchBox}>
          <FaSearch style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Şirkət adı, email və ya VÖEN ilə axtarış..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
        </div>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={styles.filterSelect}
        >
          <option value="all">Hamısı</option>
          <option value="pending">Gözləyən</option>
          <option value="approved">Təsdiqlənmiş</option>
          <option value="rejected">Rədd edilmiş</option>
        </select>
      </div>

      {/* Table */}
      <ApplicationTable
        applications={filteredApplications}
        onViewDetail={handleViewDetail}
        onApprove={handleApprove}
        onReject={handleReject}
        selectedIds={selectedIds}
        onToggleSelect={toggleSelect}
        onToggleSelectAll={toggleSelectAll}
      />

      {/* Modals */}
      {showDetailModal && selectedApplication && (
        <ApplicationDetailModal
          application={selectedApplication}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedApplication(null);
          }}
          onApprove={() => {
            setShowDetailModal(false);
            handleApprove(selectedApplication);
          }}
          onReject={() => {
            setShowDetailModal(false);
            handleReject(selectedApplication);
          }}
        />
      )}

      {showApprovalModal && selectedApplication && (
        <ApprovalModal
          application={selectedApplication}
          onConfirm={handleApprovalConfirm}
          onClose={() => {
            setShowApprovalModal(false);
            setSelectedApplication(null);
          }}
        />
      )}

      {showRejectionModal && selectedApplication && (
        <RejectionModal
          application={selectedApplication}
          onConfirm={handleRejectionConfirm}
          onClose={() => {
            setShowRejectionModal(false);
            setSelectedApplication(null);
          }}
        />
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '1800px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '30px',
    flexWrap: 'wrap',
    gap: '20px',
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#1A1A2E',
    marginBottom: '5px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#999',
  },
  btnExport: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 20px',
    backgroundColor: '#4CAF50',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  stats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
    marginBottom: '30px',
  },
  statCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    padding: '25px',
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  },
  iconText: {
    fontSize: '32px',
  },
  statLabel: {
    fontSize: '13px',
    color: '#999',
    marginBottom: '5px',
  },
  statValue: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#1A1A2E',
  },
  filters: {
    display: 'flex',
    gap: '15px',
    marginBottom: '20px',
    flexWrap: 'wrap',
  },
  searchBox: {
    flex: 1,
    minWidth: '300px',
    position: 'relative',
  },
  searchIcon: {
    position: 'absolute',
    left: '15px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#999',
  },
  searchInput: {
    width: '100%',
    padding: '12px 15px 12px 45px',
    fontSize: '14px',
    border: '2px solid #E0E0E0',
    borderRadius: '8px',
    outline: 'none',
  },
  filterSelect: {
    padding: '12px 15px',
    fontSize: '14px',
    border: '2px solid #E0E0E0',
    borderRadius: '8px',
    outline: 'none',
    cursor: 'pointer',
    backgroundColor: '#fff',
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '60vh',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '5px solid #f3f3f3',
    borderTop: '5px solid #FF6B35',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  bulkBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 20px',
    backgroundColor: '#E3F2FD',
    borderRadius: '8px',
    marginBottom: '15px',
    flexWrap: 'wrap',
  },
  bulkInfo: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1A1A2E',
    flex: 1,
  },
  bulkApprove: {
    padding: '8px 16px',
    backgroundColor: '#4CAF50',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  bulkReject: {
    padding: '8px 16px',
    backgroundColor: '#F44336',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  bulkClear: {
    padding: '8px 16px',
    backgroundColor: '#9E9E9E',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};

export default ApplicationsPage;